const CONST = require('../constants')
const COLOR = require('./color')
const Matrix = require('./matrix')

const UNIFORM_WIDTH=250
const UNIFORM_HEIGHT=50

module.exports = class Decoder {
  constructor(wasm) {
    this.wasm = wasm
    this.id = -1
    this.filters = {}
    this.decoded = 0
  } // constructor

  async init(codec, pix_fmt, width, height) {
    codec = CONST.CODEC[codec] || codec
    pix_fmt = pix_fmt>=0 ? pix_fmt : (codec===CONST.CODEC.JPEG ? CONST.PIXEL_FORMAT.YUVJ420P : CONST.PIXEL_FORMAT.YUV420P)
    width = width>0 ? width : 0
    height = height>0 ? height : 0
    this.id = this.wasm.ccall(
      'createDecoder', 'number',
      ['number', 'number', 'number', 'number'],
      [codec, pix_fmt, width, height],
    )
    return this
  } // init

  async destroy() {
    if(this.id>=0) {
      this.wasm.ccall('destroyDecoder', 'number', ['number'], [this.id])
      this.id = -1
    }
  } // destroy

  async push() {
    if(this.id<0) return -1
    return this.wasm.ccall('push', 'number', ['number'], [this.id])
  } // push

  async pop() {
    if(this.id<0) return -1
    return this.wasm.ccall('pop', 'number', ['number'], [this.id])
  } // pop

  async swap(offset) {
    if(this.id<0) return -1
    return this.wasm.ccall('swap', 'number', ['number', 'number'], [this.id, parseInt(offset)||0])
  } // swap

  async decode(packet) {
    if(this.id<0) return -1
    const decoded = this.wasm.ccall('decode', 'number', ['number', 'array', 'number'], [this.id, packet, packet.byteLength])
    if(decoded>0) this.decoded+=decoded
    return decoded
  } // decode

  async decodeNAL(packet) {
    if(this.id<0) return -1
    return this.wasm.ccall('decodeNAL', 'number', ['number', 'array', 'number'], [this.id, packet, packet.byteLength])
  } // decodeNAL

  async encode(codec, pix_fmt, q) {
    if(this.id<0) return -1
    codec = CONST.CODEC[codec] || codec
    codec = codec>=0 ? codec : CONST.CODEC.RAW
    pix_fmt = CONST.PIXEL_FORMAT[pix_fmt] || pix_fmt
    pix_fmt = pix_fmt>=0 ? pix_fmt : -1
    const p_p_data = this.wasm._malloc(4)
    const p_size = this.wasm._malloc(4)
    q = q>=2 ? q : 2

    const ret = this.wasm.ccall('encode', 'number', ['number', 'number', 'number', 'number', 'number', 'number'], [this.id, p_p_data, p_size, codec, pix_fmt, q])
    let output
    if(ret>0) {
      const p_data = this.wasm.HEAPU32[p_p_data/4]
      const size = this.wasm.HEAPU32[p_size/4]
      output = this.wasm.HEAPU8.subarray(p_data, p_data+size).slice()
      this.wasm._free(p_data)
    }
    this.wasm._free(p_p_data)
    this.wasm._free(p_size)
    return output
  } // encode

  async jpeg(q) { return this.encode('JPEG', 'YUVJ420P', q) }

  async jpeg2000(q) { return this.encode('JPEG2000', 'YUVJ420P', q) }

  async yuv420p() {
    return this.encode('RAW', 'YUV420P', 2)
  } // yuv420p

  async yuv444p() {
    return this.encode('RAW', 'YUV444P', 2)
  } // yuv444p

  async y(crop) {
    let data = await this.encode('RAW', 'GRAY8', 2)
    const info = await this.frameinfo()
    if(!info) return
    let { width, height } = info
    if(data && Array.isArray(crop)) {
      let [min_x, min_y, max_x, max_y] = crop
      if(min_x<0) min_x = 0
      if(min_y<0) min_y = 0
      if(max_x<=0) max_x = width + max_x
      if(max_y<=0) max_y = height + max_y
      if(max_x>=width) max_x=width-1
      if(max_y>=height) max_y=height-1
      const new_width = max_x-min_x+1
      const new_height = max_y-min_y+1
      if(new_width<=0 || new_height<=0) return
      const extract = new Uint8Array(new_width*new_height)
      const new_linesize = new_width
      for(let y=0; y<new_height; y++) {
        const p = ((y+min_y)*width + min_x)
        const line = data.subarray(p, p+new_linesize)
        extract.set(line, y*new_linesize)
      }
      data = extract
      width = new_width
      height = new_height
    }
    return { width, height, data }
  } // y

  async rgb(crop) {
    let data = await this.encode('RAW', 'RGB24', 2)
    const info = await this.frameinfo()
    if(!info) return
    let { width, height } = info
    if(data && Array.isArray(crop)) {
      let [min_x, min_y, max_x, max_y] = crop
      if(min_x<0) min_x = 0
      if(min_y<0) min_y = 0
      if(max_x<=0) max_x = width + max_x
      if(max_y<=0) max_y = height + max_y
      if(max_x>=width) max_x=width-1
      if(max_y>=height) max_y=height-1
      const new_width = max_x-min_x+1
      const new_height = max_y-min_y+1
      if(new_width<=0 || new_height<=0) return
      const extract = new Uint8Array(3*new_width*new_height)
      const new_linesize = 3*new_width
      for(let y=0; y<new_height; y++) {
        const p = 3*((y+min_y)*width + min_x)
        const line = data.subarray(p, p+new_linesize)
        extract.set(line, y*new_linesize)
      }
      data = extract
      width = new_width
      height = new_height
    }
    return { width, height, data }
  } // rgb

  async rgba(crop) {
    let data = await this.encode('RAW', 'RGBA', 2)
    const info = await this.frameinfo()
    if(!info) return
    let { width, height } = info
    if(data && Array.isArray(crop)) {
      let [min_x, min_y, max_x, max_y] = crop
      if(min_x<0) min_x = 0
      if(min_y<0) min_y = 0
      if(max_x<=0) max_x = width + max_x
      if(max_y<=0) max_y = height + max_y
      if(max_x>=width) max_x=width-1
      if(max_y>=height) max_y=height-1
      const new_width = max_x-min_x+1
      const new_height = max_y-min_y+1
      if(new_width<=0 || new_height<=0) return
      const extract = new Uint8Array(4*new_width*new_height)
      const new_linesize = 4*new_width
      for(let y=0; y<new_height; y++) {
        const p = 4*((y+min_y)*width + min_x)
        const line = data.subarray(p, p+new_linesize)
        extract.set(line, y*new_linesize)
      }
      data = extract
      width = new_width
      height = new_height
    }
    return { width, height, data }
  } // rgba

  async matrix() {
    const frame = await this.frameinfo()
    if(!frame) return
    const gray = await this.encode('RAW', 'GRAY8')
    if(!gray) return
    return new Matrix(gray, frame.width, frame.height)
  } // matrix

  async frameinfo() {
    if(this.id<0) return -1
    const param_names = ['width', 'height', 'size', 'pix_fmt', 'pict_type', 'color_range', 'color_space', 'line_size1', 'line_size2', 'line_size3', 'line_size4']
    const param_pointers = param_names.map(()=>this.wasm._malloc(4))

    const ret = this.wasm.ccall('frameinfo', 'number', ['number'].concat(param_names.map(()=>'number')), [this.id, ...param_pointers])
    let output
    if(ret>0) {
      output = {}
      for(let i=0; i<param_names.length; i++) output[param_names[i]] = this.wasm.HEAPU32[param_pointers[i]/4]
    }

    param_pointers.forEach(p=>this.wasm._free(p))
    return output
  } // frameinfo

  async resize(width, height, pix_fmt, algorithm) {
    if(this.id<0) return -1
    width = parseInt(width) || 0
    height = parseInt(height) || 0
    pix_fmt = CONST.PIXEL_FORMAT[pix_fmt] || pix_fmt
    pix_fmt = pix_fmt>=0 ? pix_fmt : -1 // -1 will keep original pixel format
    algorithm = algorithm>0 ? algorithm : -1
    return this.wasm.ccall('resize', 'number', ['number', 'number', 'number', 'number', 'number'], [this.id, width, height, pix_fmt, algorithm])
  } // resize

  async changePixelFormat(pix_fmt) {
    if(this.id<0) return -1
    pix_fmt = CONST.PIXEL_FORMAT[pix_fmt] || pix_fmt
    if(!(pix_fmt>=0)) return 0
    return this.wasm.ccall('changePixelFormat', 'number', ['number', 'number'], [this.id, pix_fmt])
  } // changePixelFormat

  async filter(filter_def) {
    if(this.id<0) return -1

    const frame = await this.frameinfo()
    if(!frame) return -2

    const input_def = `video_size=${frame.width}x${frame.height}:pix_fmt=${frame.pix_fmt}:time_base=1/25:pixel_aspect=1/1`
    const output_def = `pix_fmt=${frame.pix_fmt}`

    const filters = this.filters[filter_def] || {}
    let filter_id = filters[input_def+'>'+output_def]

    if(!(filter_id>=0)) {
      filter_id = this.wasm.ccall('createFilter', 'number', ['number', 'string', 'string', 'string'], [this.id, input_def, filter_def, output_def])
      if(!(filter_id>=0)) return -2
      if(!this.filters[filter_def]) this.filters[filter_def] = {}
      this.filters[filter_def][input_def+'>'+output_def] = filter_id
    }

    return this.wasm.ccall('filter', 'number', ['number', 'number'], [this.id, filter_id])
  } // filter

  async destroyFilter(filter_def) {
    if(this.id<0) return -1
    const filter_ids = Object.values(this.filters[filter_def] || {})
    for(const filter_id of filter_ids) this.wasm.ccall('destroyFilter', 'number', ['number', 'number'], [this.id, filter_id])
    delete this.filters[filter_def]
    return 0
  } // destroyFilter

  async destroyFilters() {
    if(this.id<0) return -1
    return this.wasm.ccall('destroyFilters', 'number', ['number'], [this.id])
  } // destroyFilter

  async placePushed(x, y, keep) {
    if(this.id<0) return -1
    return this.wasm.ccall('placePushed', 'number', ['number', 'number', 'number'], [this.id, x, y, keep?1:0])
  } // placePushed

  async placeIntoPushed(x, y) {
    if(this.id<0) return -1
    return this.wasm.ccall('placeIntoPushed', 'number', ['number', 'number', 'number'], [this.id, x, y])
  } // placeIntoPushed

  async drawLine(x1, y1, x2, y2, color, thick) {
    if(this.id<0) return -1

    x1 = parseInt(x1) || 0
    x1 = x1>65535 ? 65535 : x1>0 ? x1 : 0
    y1 = parseInt(y1) || 0
    y1 = y1>65535 ? 65535 : y1>0 ? y1 : 0
    x2 = parseInt(x2) || 0
    x2 = x2>65535 ? 65535 : x2>0 ? x2 : 0
    y2 = parseInt(y2) || 0
    y2 = y2>65535 ? 65535 : y2>0 ? y2 : 0

    const [r, g, b, opacity] = COLOR.color2rgba(color) || [0, 0, 0, 255]

    thick = parseInt(thick) || 0
    thick = thick>255 ? 255 : thick>1 ? thick : 1

    const ret = this.wasm.ccall(
      'drawLine', 'number',
      ['number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number'],
      [this.id, x1, y1, x2, y2, thick, r, g, b, opacity],
    )

    return ret
  } // drawLine

  async drawText(text, fontsize, x, y, fontcolor, fontfile) {
    let options = {}
    if(typeof text === 'object') {
      options = text
    }else{
      options = {
        fontfile,
        fontsize,
        fontcolor,
        x,
        y,
        text,
      }
    }

    fontfile = options.fontfile || 'roboto_mono_regular'
    if(/^(regular|bold|light)$/.test(fontfile)) fontfile = '/roboto_mono_'+fontfile+'.ttf'
    if(/^\w+$/.test(fontfile)) fontfile = '/'+fontfile+'.ttf'
    fontsize = parseInt(options.fontsize)
    if(!(fontsize>0)) fontsize = 16
    text = String(options.text??'')
    if(text==='') return -1

    text = text
      .replace(/\\/g, '\\\\')
      .replace(/'/g, "\\'")
      .replace(/:/g, '\\:')
      .replace(/,/g, '\\,')
      .replace(/!/g, '\\!')

    x = parseInt(options.x) || 0
    x = x>65535 ? 65535 : x>0 ? x : 0
    y = parseInt(options.y) || 0
    y = y>65535 ? 65535 : y>0 ? y : 0
    const [r, g, b, a] = COLOR.color2rgba(options.fontcolor) || [0, 0, 0, 255]
    fontcolor = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}${a.toString(16).padStart(2, '0')}`

    const filter_def = `drawtext=expansion=none:fontfile=${fontfile}:fontsize=${fontsize}:fontcolor=${fontcolor}:x=${x}:y=${y}:text='${text}'`
    await this.filter(filter_def)
    await this.destroyFilter(filter_def)
    return 0
  } // drawText

  async drawBox(x, y, w, h, fillcolor, bordercolor, borderthick) {
    x = parseInt(x) || 0
    x = x>65535 ? 65535 : x>0 ? x : 0
    y = parseInt(y) || 0
    y = y>65535 ? 65535 : y>0 ? y : 0
    w = parseInt(w) || 0
    w = w>65535 ? 65535 : w>0 ? w : 0
    h = parseInt(h) || 0
    h = h>65535 ? 65535 : h>0 ? h : 0
    fillcolor = COLOR.color2rgba(fillcolor)
    bordercolor = COLOR.color2rgba(bordercolor)
    borderthick = parseInt(borderthick) || 0

    let filter_def = []
    if(fillcolor) {
      const [r, g, b, a] = fillcolor
      fillcolor = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}${a.toString(16).padStart(2, '0')}`
      filter_def.push(`drawbox=x=${x}:y=${y}:w=${w}:h=${h}:color=${fillcolor}:t=fill`)
    }
    if(bordercolor && borderthick>0) {
      const [r, g, b, a] = bordercolor
      bordercolor = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}${a.toString(16).padStart(2, '0')}`
      filter_def.push(`drawbox=x=${x}:y=${y}:w=${w}:h=${h}:color=${bordercolor}:t=${borderthick}`)
    }
    if(filter_def.length===0) return -1

    filter_def = filter_def.join(',')
    await this.filter(filter_def)
    await this.destroyFilter(filter_def)
    return 0
  } // drawBox

  async sobelMaskMatrix(threshold, min_x, min_y, max_x, max_y) {
    return this.sobelMask(threshold, min_x, min_y, max_x, max_y, false, true)
  } // sobelMaskMatrix

  async sobelMaskPixels(threshold, min_x, min_y, max_x, max_y) {
    return this.sobelMask(threshold, min_x, min_y, max_x, max_y, true, false)
  } // sobelMaskPixels

  async sobelMask(threshold, min_x, min_y, max_x, max_y, exclude_matrix, exclude_pixels) {
    if(this.id<0) return -1

    min_x = min_x>0 ? min_x : 0
    min_y = min_y>0 ? min_y : 0
    max_x = max_x>0 ? max_x : 9999
    max_y = max_y>0 ? max_y : 9999

    const p_min_x = this.wasm._malloc(4)
    const p_min_y = this.wasm._malloc(4)
    const p_max_x = this.wasm._malloc(4)
    const p_max_y = this.wasm._malloc(4)
    const p_p_matrix = this.wasm._malloc(4)
    const p_p_pixels = this.wasm._malloc(4)
    const p_pixels_count = this.wasm._malloc(4)

    this.wasm.HEAPU32[p_min_x/4] = min_x
    this.wasm.HEAPU32[p_min_y/4] = min_y
    this.wasm.HEAPU32[p_max_x/4] = max_x
    this.wasm.HEAPU32[p_max_y/4] = max_y

    const ret = this.wasm.ccall(
      'sobelMask', 'number',
      ['number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number'],
      [this.id, threshold, p_min_x, p_min_y, p_max_x, p_max_y, exclude_matrix?0:p_p_matrix, exclude_pixels?0:p_p_pixels, p_pixels_count])
    let output
    if(ret>=0) {
      min_x = this.wasm.HEAPU32[p_min_x/4]
      min_y = this.wasm.HEAPU32[p_min_y/4]
      max_x = this.wasm.HEAPU32[p_max_x/4]
      max_y = this.wasm.HEAPU32[p_max_y/4]
      const width = (max_x-min_x+1)
      const height = (max_y-min_y+1)
      const matrix_bytes = width*height
      const pixels_count = this.wasm.HEAPU32[p_pixels_count/4]
      const pixels_bytes = pixels_count * 4 // 2 bytes for X, 2 bytes for Y
      const p_matrix = exclude_matrix ? undefined : this.wasm.HEAPU32[p_p_matrix/4]
      const p_pixels = exclude_pixels ? undefined : this.wasm.HEAPU32[p_p_pixels/4]
      output = {
        min_x,
        min_y,
        max_x,
        max_y,
        width,
        height,
        matrix: this.wasm.HEAPU8.subarray(p_matrix, p_matrix+matrix_bytes).slice(),
        pixels: this.wasm.HEAPU8.subarray(p_pixels, p_pixels+pixels_bytes).slice(),
        pixels_count,
      }
      if(p_matrix) this.wasm._free(p_matrix)
      if(p_pixels) this.wasm._free(p_pixels)
    }
    this.wasm._free(p_min_x)
    this.wasm._free(p_min_y)
    this.wasm._free(p_max_x)
    this.wasm._free(p_max_y)
    this.wasm._free(p_p_matrix)
    this.wasm._free(p_p_pixels)
    this.wasm._free(p_pixels_count)

    return output
  } // sobelMask

  async sobelClusters(threshold, min_x, min_y, max_x, max_y, min_length_h, min_length_v, max_angle_h, max_angle_v) {
    if(this.id<0) return -1

    min_x = min_x>0 ? min_x : 0
    min_y = min_y>0 ? min_y : 0
    max_x = max_x>0 ? max_x : 9999
    max_y = max_y>0 ? max_y : 9999
    min_length_h = min_length_h>0 ? min_length_h : 0
    min_length_v = min_length_v>0 ? min_length_v : 0
    max_angle_h = max_angle_h>0 ? max_angle_h : 9999
    max_angle_v = max_angle_v>0 ? max_angle_v : 9999

    const p_min_x = this.wasm._malloc(4)
    const p_min_y = this.wasm._malloc(4)
    const p_max_x = this.wasm._malloc(4)
    const p_max_y = this.wasm._malloc(4)
    const p_p_clusters = this.wasm._malloc(4)
    const p_direction = this.wasm._malloc(4)
    const p_x1 = this.wasm._malloc(4)
    const p_y1 = this.wasm._malloc(4)
    const p_x2 = this.wasm._malloc(4)
    const p_y2 = this.wasm._malloc(4)
    const p_p_edge = this.wasm._malloc(4)
    const p_edge_count = this.wasm._malloc(4)
    const p_p_pixels = this.wasm._malloc(4)
    const p_pixels_count = this.wasm._malloc(4)

    this.wasm.HEAPU32[p_min_x/4] = min_x
    this.wasm.HEAPU32[p_min_y/4] = min_y
    this.wasm.HEAPU32[p_max_x/4] = max_x
    this.wasm.HEAPU32[p_max_y/4] = max_y

    const count = this.wasm.ccall(
      'sobel_clusters', 'number',
      ['number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number'],
      [this.id, threshold, p_min_x, p_min_y, p_max_x, p_max_y, min_length_h, min_length_v, max_angle_h, max_angle_v, p_p_clusters])
    let output
    if(count>=0) {
      min_x = this.wasm.HEAPU32[p_min_x/4]
      min_y = this.wasm.HEAPU32[p_min_y/4]
      max_x = this.wasm.HEAPU32[p_max_x/4]
      max_y = this.wasm.HEAPU32[p_max_y/4]
      const width = (max_x-min_x+1)
      const height = (max_y-min_y+1)
      output = {
        min_x,
        min_y,
        max_x,
        max_y,
        width,
        height,
        clusters: [],
      }

      const p_clusters = this.wasm.HEAPU32[p_p_clusters/4]
      for(let i=0; i<count; i++) {
        const ret = this.wasm.ccall(
          'cluster_info', 'number',
          ['number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number'],
          [p_clusters, i, p_direction, p_x1, p_y1, p_x2, p_y2, p_p_edge, p_edge_count, p_p_pixels, p_pixels_count])
        if(ret>=0) {
          const direction = this.wasm.HEAPU16[p_direction/2]
          const x1 = this.wasm.HEAPU16[p_x1/2]
          const y1 = this.wasm.HEAPU16[p_y1/2]
          const x2 = this.wasm.HEAPU16[p_x2/2]
          const y2 = this.wasm.HEAPU16[p_y2/2]
          const p_edge = this.wasm.HEAPU32[p_p_edge/4]
          const edge_count = this.wasm.HEAPU16[p_edge_count/2]
          const p_pixels = this.wasm.HEAPU32[p_p_pixels/4]
          const pixels_count = this.wasm.HEAPU16[p_pixels_count/2]
          let edge=[]; let pixels=[]
          if(p_edge) {
            edge = this.wasm.HEAPU16.subarray(p_edge/2, (p_edge+edge_count*2)/2).slice()
            // we don't do this.wasm._free(p_edge) because edga data will be actually cleared by clusters_free() below
          }
          if(p_pixels) {
            pixels = this.wasm.HEAPU16.subarray(p_pixels/2, (p_pixels+pixels_count*4)/2).slice()
            // we don't do this.wasm._free(p_pixels) because edga data will be actually cleared by clusters_free() below
          }
          output.clusters.push({ i, direction, x1, y1, x2, y2, edge, pixels })
        }
      }

      if(p_clusters) {
        this.wasm.ccall('clusters_free', null, ['number'], [p_clusters])
        this.wasm._free(p_clusters)
      }
    }

    this.wasm._free(p_min_x)
    this.wasm._free(p_min_y)
    this.wasm._free(p_max_x)
    this.wasm._free(p_max_y)
    this.wasm._free(p_p_clusters)
    this.wasm._free(p_direction)
    this.wasm._free(p_x1)
    this.wasm._free(p_y1)
    this.wasm._free(p_x2)
    this.wasm._free(p_y2)
    this.wasm._free(p_p_edge)
    this.wasm._free(p_edge_count)
    this.wasm._free(p_p_pixels)
    this.wasm._free(p_pixels_count)

    return output
  } // sobelMask

  async findPlates(threshold, min_x, min_y, max_x, max_y, min_length_h, min_length_v, max_angle_h, max_angle_v) {
    if(this.id<0) return -1

    min_x = min_x>0 ? min_x : 0
    min_y = min_y>0 ? min_y : 0
    max_x = max_x>0 ? max_x : 9999
    max_y = max_y>0 ? max_y : 9999
    min_length_h = min_length_h>0 ? min_length_h : 0
    min_length_v = min_length_v>0 ? min_length_v : 0
    max_angle_h = max_angle_h>0 ? max_angle_h : 9999
    max_angle_v = max_angle_v>0 ? max_angle_v : 9999

    const p_min_x = this.wasm._malloc(4)
    const p_min_y = this.wasm._malloc(4)
    const p_max_x = this.wasm._malloc(4)
    const p_max_y = this.wasm._malloc(4)
    const p_p_plates = this.wasm._malloc(4)

    this.wasm.HEAPU32[p_min_x/4] = min_x
    this.wasm.HEAPU32[p_min_y/4] = min_y
    this.wasm.HEAPU32[p_max_x/4] = max_x
    this.wasm.HEAPU32[p_max_y/4] = max_y

    const count = this.wasm.ccall(
      'find_plates', 'number',
      ['number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number'],
      [this.id, threshold, p_min_x, p_min_y, p_max_x, p_max_y, min_length_h, min_length_v, max_angle_h, max_angle_v, p_p_plates])
    let output
    if(count>=0) {
      min_x = this.wasm.HEAPU32[p_min_x/4]
      min_y = this.wasm.HEAPU32[p_min_y/4]
      max_x = this.wasm.HEAPU32[p_max_x/4]
      max_y = this.wasm.HEAPU32[p_max_y/4]
      const width = (max_x-min_x+1)
      const height = (max_y-min_y+1)
      output = { min_x, min_y, max_x, max_y, width, height, plates: [] }
      const p_plates = this.wasm.HEAPU32[p_p_plates/4]
      const view = new DataView(this.wasm.HEAPU8.buffer)
      for(let i=0; i<count; i++) {
        const offset = p_plates + i*20
        const plate = {
          tlx: view.getUint16(offset, true),
          tly: view.getUint16(offset+2, true),
          trx: view.getUint16(offset+4, true),
          try: view.getUint16(offset+6, true),
          blx: view.getUint16(offset+8, true),
          bly: view.getUint16(offset+10, true),
          brx: view.getUint16(offset+12, true),
          bry: view.getUint16(offset+14, true),
          area: view.getUint32(offset+16, true),
        }
        plate.size = Math.round(Math.sqrt(plate.area))
        output.plates.push(plate)
      }
      this.wasm._free(p_plates)
    }

    this.wasm._free(p_min_x)
    this.wasm._free(p_min_y)
    this.wasm._free(p_max_x)
    this.wasm._free(p_max_y)
    this.wasm._free(p_p_plates)

    return output
  } // findPlates

  async setupLimits(width, height) {
    if(this.id<0) return
    const frame = await this.frameinfo()
    if(!frame) return
    let border_h = Math.floor((frame.width-width)/2)
    let border_v = Math.floor((frame.height-height)/2)
    if(border_h<0) border_h=0
    if(border_v<0) border_v=0
    return [border_h, border_v, frame.width-border_h-1, frame.height-border_v-1]
  } // setupLimits

  async extractGray() {
    if(this.id<0) return
  } // extractGray

  async uniformGray(corners, width, height) {
    if(this.id<0) return
    width = width>0 ? width : UNIFORM_WIDTH
    height = height>0 ? height : UNIFORM_HEIGHT
    if(!(width>0 && height>0)) return
    const size = width*height
    const p_uniform = this.wasm._malloc(size)
    const ret = this.wasm.ccall(
      'uniformGray', 'number',
      ['number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number'],
      [this.id, corners.tlx, corners.tly, corners.trx, corners.try, corners.blx, corners.bly, corners.brx, corners.bry, width, height, p_uniform])
    const uniform = ret>=0 ? new Matrix(this.wasm.HEAPU8.subarray(p_uniform, p_uniform+size), width, height) : undefined
    this.wasm._free(p_uniform)
    return uniform
  } // uniformGray
} // Decoder
